import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Inbox, Pencil, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState, PageHeader } from "@/components/common/ui-bits";
import { EntryRow } from "@/components/entry/EntryRow";
import { EditEntryDialog } from "@/components/entry/EditEntryDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { BulkBar, BulkDeleteDialog } from "@/components/common/BulkBar";
import { PhotoThumb } from "@/components/scan/Scan";
import { api, errMsg } from "@/lib/api";
import { formatDate, money, paymentLabel } from "@/lib/format";

const BillLink = ({ t }) => (
  <span className="inline-flex items-center gap-2">
    <span className="rounded-full bg-accent px-2 py-0.5 text-[11px] font-semibold text-accent-foreground" data-testid={`bill-payment-badge-${t.id}`}>Bill payment</span>
    <Link to={`/bills?bill=${t.bill_id}`} className="text-xs font-semibold text-sky-700 hover:underline" data-testid={`bill-payment-open-${t.id}`}>Open bill</Link>
  </span>
);

const EMPTY = { start: "", end: "", type: "all", category_id: "all", vendor: "", q: "" };
const PAGE_SIZE = 50;

function Filters({ filters, setFilters, categories }) {
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const cats = categories.filter((c) => filters.type === "all" || c.type === filters.type);
  return (
    <div className="surface p-4 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 fade-up" data-testid="transaction-filters">
      <div className="min-w-0"><label className="eyebrow">From</label>
        <Input type="date" value={filters.start} onChange={(e) => set("start", e.target.value)} className="mt-1.5 h-10 rounded-xl" data-testid="filter-start-date" /></div>
      <div className="min-w-0"><label className="eyebrow">To</label>
        <Input type="date" value={filters.end} onChange={(e) => set("end", e.target.value)} className="mt-1.5 h-10 rounded-xl" data-testid="filter-end-date" /></div>
      <div className="min-w-0"><label className="eyebrow">Type</label>
        <Select value={filters.type} onValueChange={(v) => setFilters((f) => ({ ...f, type: v, category_id: "all" }))}>
          <SelectTrigger className="mt-1.5 h-10 rounded-xl" data-testid="filter-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="income" data-testid="filter-type-income">Income</SelectItem>
            <SelectItem value="expense" data-testid="filter-type-expense">Expense</SelectItem>
          </SelectContent>
        </Select></div>
      <div className="min-w-0"><label className="eyebrow">Category</label>
        <Select value={filters.category_id} onValueChange={(v) => set("category_id", v)}>
          <SelectTrigger className="mt-1.5 h-10 rounded-xl" data-testid="filter-category-select"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">All categories</SelectItem>
            {cats.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`filter-category-option-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{c.name}{c.is_archived ? " (archived)" : ""}</SelectItem>)}
          </SelectContent>
        </Select></div>
      <div className="min-w-0"><label className="eyebrow">Vendor / payee</label>
        <Input value={filters.vendor} onChange={(e) => set("vendor", e.target.value)} placeholder="Exact name" className="mt-1.5 h-10 rounded-xl" data-testid="filter-vendor-input" /></div>
      <div className="min-w-0"><label className="eyebrow">Search</label>
        <Input value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="Vendor, note, category" className="mt-1.5 h-10 rounded-xl" data-testid="filter-search-input" /></div>
    </div>
  );
}

function TotalsBar({ totals, count, onClear, hasFilters }) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-2xl bg-foreground text-white px-5 py-3.5 my-5 fade-up" data-testid="filtered-totals">
      <span className="text-xs font-semibold uppercase tracking-widest text-white/70">Filtered total · {count} {count === 1 ? "entry" : "entries"}</span>
      <span className="text-sm">Income <b className="font-num text-emerald-300" data-testid="filtered-income">{money(totals.income_cents)}</b></span>
      <span className="text-sm">Expenses <b className="font-num text-rose-300" data-testid="filtered-expenses">{money(totals.expense_cents)}</b></span>
      <span className="text-sm">Net <b className="font-num" data-testid="filtered-net">{money(totals.net_cents)}</b></span>
      {hasFilters && <button onClick={onClear} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-white/80 hover:text-white" data-testid="filter-clear-button"><X className="h-3.5 w-3.5" />Clear filters</button>}
    </div>
  );
}

function TxTable({ items, onEdit, onDelete, sel }) {
  return (
    <div className="hidden md:block surface overflow-hidden">
      <table className="w-full text-sm" data-testid="transactions-table">
        <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
          <tr><th className="pl-4 py-3 w-8"><Checkbox checked={sel.pageAll} onCheckedChange={sel.togglePage} aria-label="Select page" data-testid="transactions-select-page" /></th>
            <th className="px-4 py-3">Date</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Vendor / payee</th>
            <th className="px-4 py-3">Description</th><th className="px-4 py-3">Method</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3 w-24" /></tr>
        </thead>
        <tbody className="divide-y divide-border">
          {items.map((t) => (
            <tr key={t.id} className={`hover:bg-secondary/30 transition-colors ${sel.has(t.id) ? "bg-rose-50/60" : ""}`} data-testid={`transaction-row-${t.id}`}>
              <td className="pl-4 py-3"><Checkbox checked={sel.has(t.id)} onCheckedChange={() => sel.toggle(t.id)} aria-label="Select entry" data-testid={`transaction-select-${t.id}`} /></td>
              <td className="px-4 py-3 whitespace-nowrap"><div className="flex items-center gap-2">{formatDate(t.date)}{t.photo_id && <PhotoThumb photoId={t.photo_id} size="h-8 w-8" testId={`transaction-photo-${t.id}`} />}</div></td>
              <td className="px-4 py-3">{t.category_name}{t.category_archived && <span className="ml-1 text-xs text-muted-foreground">(archived)</span>}</td>
              <td className="px-4 py-3">{t.vendor || <span className="text-muted-foreground">—</span>}</td>
              <td className="px-4 py-3 max-w-[260px] truncate text-muted-foreground" title={t.description}>{t.description}</td>
              <td className="px-4 py-3 whitespace-nowrap">{paymentLabel(t.payment_method)}</td>
              <td className={`px-4 py-3 text-right font-num font-semibold whitespace-nowrap ${t.type === "income" ? "text-emerald-700" : "text-rose-600"}`} data-testid={`transaction-amount-${t.id}`}>
                {t.type === "income" ? "+" : "−"}{money(t.amount_cents)}</td>
              <td className="px-2 py-3 text-right whitespace-nowrap">
                {t.bill_id ? <BillLink t={t} /> : <>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(t)} data-testid={`transaction-edit-${t.id}`} aria-label="Edit"><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => onDelete(t)} data-testid={`transaction-delete-${t.id}`} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button></>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function TransactionsPage() {
  const [filters, setFilters] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [skipNote, setSkipNote] = useState("");

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const activeFilters = useCallback(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") p[k] = v; });
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    const { data: d } = await api.get("/transactions", { params: { page, page_size: PAGE_SIZE, ...activeFilters() } });
    setData(d);
  }, [activeFilters, page]);

  const clearSelection = () => { setSelected(new Set()); setAllMatching(false); };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); clearSelection(); }, [filters]);

  const pageIds = data?.items.map((t) => t.id) || [];
  const pageAll = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const sel = {
    has: (id) => allMatching || selected.has(id),
    pageAll: allMatching || pageAll,
    toggle: (id) => {
      setAllMatching(false);
      setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    },
    togglePage: () => {
      setAllMatching(false);
      setSelected((s) => { const n = new Set(s); pageIds.forEach((id) => (pageAll ? n.delete(id) : n.add(id))); return n; });
    },
  };
  const selCount = allMatching ? data?.total_items || 0 : selected.size;

  const bulkDelete = async () => {
    setBulkBusy(true);
    try {
      const body = allMatching ? { all_matching: true, filters: activeFilters() } : { ids: [...selected] };
      const { data: r } = await api.post("/transactions/bulk-delete", body);
      toast.success(`${r.deleted} ${r.deleted === 1 ? "entry" : "entries"} deleted`);
      setSkipNote(r.message || "");
      if (r.message) toast.warning(r.message);
      setBulkOpen(false); clearSelection(); load();
    } catch (e) { toast.error(errMsg(e)); }
    setBulkBusy(false);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/transactions/${deleting.id}`);
      toast.success("Entry deleted");
      setDeleting(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const hasFilters = JSON.stringify(filters) !== JSON.stringify(EMPTY);
  const pages = data ? Math.max(1, Math.ceil(data.total_items / PAGE_SIZE)) : 1;

  return (
    <div>
      <PageHeader eyebrow="Every entry, all users" title="Transactions" testId="transactions-title">
        <Button asChild className="rounded-xl h-10" data-testid="transactions-add-entry-button"><Link to="/add"><Plus className="h-4 w-4 mr-1" />Add Entry</Link></Button>
      </PageHeader>
      <Filters filters={filters} setFilters={setFilters} categories={categories} />
      {data && <TotalsBar totals={data.totals} count={data.total_items} hasFilters={hasFilters} onClear={() => setFilters(EMPTY)} />}
      {skipNote && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900" data-testid="bulk-skipped-message">
          <span>{skipNote}</span>
          <button onClick={() => setSkipNote("")} aria-label="Dismiss" data-testid="bulk-skipped-dismiss"><X className="h-4 w-4" /></button>
        </div>
      )}
      <BulkBar count={selCount} total={data?.total_items} testPrefix="transactions"
        canSelectAll={!allMatching && pageAll && data?.total_items > selected.size}
        onSelectAll={() => setAllMatching(true)} onClear={clearSelection} onDelete={() => setBulkOpen(true)} />
      <BulkDeleteDialog open={bulkOpen} busy={bulkBusy} testPrefix="transactions" title={`Delete ${selCount} entries? This can't be undone.`}
        onCancel={() => setBulkOpen(false)} onConfirm={bulkDelete} />
      {!data ? <div className="text-sm text-muted-foreground mt-6">Loading…</div> : data.items.length === 0 ? (
        <div className="surface"><EmptyState icon={Inbox} text="No transactions found" testId="transactions-empty" /></div>
      ) : (
        <>
          <TxTable items={data.items} onEdit={setEditing} onDelete={setDeleting} sel={sel} />
          <div className="md:hidden surface divide-y divide-border overflow-hidden" data-testid="transactions-list-mobile">
            <label className="flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-muted-foreground bg-secondary/40">
              <Checkbox checked={sel.pageAll} onCheckedChange={sel.togglePage} data-testid="transactions-select-page-mobile" />Select this page
            </label>
            {data.items.map((t) => (
              <EntryRow key={t.id} entry={t} showCreator actions={<>
                <Checkbox checked={sel.has(t.id)} onCheckedChange={() => sel.toggle(t.id)} className="h-6 w-6 self-center" aria-label="Select entry" data-testid={`transaction-select-mobile-${t.id}`} />
                {t.bill_id ? <BillLink t={t} /> : <>
                <Button size="sm" variant="outline" className="h-8 rounded-lg" onClick={() => setEditing(t)} data-testid={`transaction-edit-mobile-${t.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                <Button size="sm" variant="outline" className="h-8 rounded-lg text-rose-600 border-rose-200" onClick={() => setDeleting(t)} data-testid={`transaction-delete-mobile-${t.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />Delete</Button>
              </>}</>} />
            ))}
          </div>
          {pages > 1 && (
            <div className="mt-4 flex items-center justify-center gap-3 text-sm" data-testid="transactions-pagination">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="transactions-prev-page">Previous</Button>
              <span className="text-muted-foreground">Page {page} of {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="transactions-next-page">Next</Button>
            </div>
          )}
        </>
      )}
      <EditEntryDialog entry={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-card rounded-2xl" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting && `${deleting.category_name} · ${money(deleting.amount_cents)} on ${formatDate(deleting.date)}. This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-confirm-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
